/**
 * Types pour l'import intelligent d'events
 * PRD : docs/PRDs/PRD-import-intelligent-events.md
 */

// Tags disponibles pour l'import (meme liste que PREDEFINED_TAGS sans 'free' et 'favorites')
export type ImportTag = 'music' | 'sports' | 'art' | 'food' | 'culture' | 'yoga' | 'spirituality' | 'children' | 'party';

// Source de l'import
export type ImportSource = 'photo' | 'pdf' | 'excel' | 'csv' | 'instagram_link' | 'instagram_screenshot' | 'text';

// Event simple importé (retourné par Gemini)
export interface ImportedSingleEvent {
  type: 'single';
  title: string;
  date: string | null;          // YYYY-MM-DD
  time_start: string | null;    // HH:MM
  time_end: string | null;      // HH:MM (null → system adds +2h via ensureEndTime)
  duration_minutes: number | null;
  location: string | null;
  description: string;          // always present: from source OR generated by Gemini
  price: number | null;
  currency: string | null;
  tags: ImportTag[];             // 1 to 3 tags
  confidence: number;           // 0-1
  image_url: string | null;     // URL detected in Excel/CSV columns
}

// Event récurrent importé (retourné par Gemini)
export interface ImportedRecurringEvent {
  type: 'recurring';
  title: string;
  time_start: string;           // HH:MM (required) — uniform time, or first day's time if day_schedules is set
  time_end: string | null;      // null → system adds +2h
  frequency: 'daily' | 'weekly';
  week_days: string[];          // required if weekly, empty [] if daily
  day_schedules?: Record<string, { start: string; end: string }> | null; // Per-day time overrides (when times vary by day)
  start_date?: string;          // YYYY-MM-DD (default: next matching day)
  end_type: 'date' | 'occurrences';
  end_date?: string | null;     // YYYY-MM-DD (required if end_type='date')
  occurrences?: number | null;  // number of WEEKS (default: 3, if end_type='occurrences')
  location: string | null;
  description: string;
  price: number | null;
  currency: string | null;
  tags: ImportTag[];             // 1 to 3 tags
  confidence: number;
  image_url: string | null;
}

export type ImportedEvent = ImportedSingleEvent | ImportedRecurringEvent;

// Résultat de l'import depuis l'Edge Function
export interface ImportResult {
  source: ImportSource;
  events: ImportedEvent[];
  cover_image_url: string | null;
  raw_file_names: string[];
  file_hashes: string[];        // SHA-256 hashes for duplicate detection
  processed_at: string;
}

// Breakdown des niveaux de confiance pour le result screen
export interface ConfidenceBreakdown {
  high: number;   // >= 0.8
  medium: number;  // 0.5 - 0.79
  low: number;     // < 0.5
}

// État du flow d'import dans le modal
export interface ImportState {
  step: 'upload' | 'analyzing' | 'result' | 'error';
  files: File[];
  instagramUrl: string | null;
  result: ImportResult | null;
  draftsCreated: number;
  seriesCreated: number;
  duplicatesFound: number;
  confidenceBreakdown: ConfidenceBreakdown;
  isLoading: boolean;
  loadingMessage: string;
  error: string | null;
  errorDetail?: string | null;
  createdEventIds: string[];    // IDs of created draft events (for post-import actions like cover photo)
  hasImage: boolean;            // Whether the import already has a cover image
  firstEventContext: {          // Context for AI image generation prompt (aggregated from all imported events)
    title: string;              // First event title (fallback)
    description: string;        // First event description (fallback)
    tags: string[];             // All unique tags across imported events
    allTitles: string[];        // All unique event titles
    organizerName?: string;     // Organizer name if available
    organizerBio?: string;      // Organizer bio/tagline if available
  } | null;
}

// Payload envoyé à l'Edge Function
export interface ImportPayload {
  files: Array<{
    name: string;
    type: string;
    data: string; // base64
  }>;
  instagram_url?: string;
  spreadsheet_data?: string; // parsed Excel/CSV as text
  freeform_text?: string; // free text pasted by user (WhatsApp, description, etc.)
  organizer_context: {
    location: string | null;
    location_name: string | null;
    latitude: number | null;
    longitude: number | null;
    timezone: string;
  };
  recent_events: Array<Record<string, unknown>>; // 5 recent events as JSON examples
  current_date: string; // YYYY-MM-DD
}

// Réponse de l'Edge Function
export interface ImportApiResponse {
  success: boolean;
  events: ImportedEvent[];
  instagram_thumbnail_url?: string;
  error?: string;
}

// Confiance badge levels
export type ConfidenceLevel = 'high' | 'medium' | 'low';

export interface ConfidenceConfig {
  level: ConfidenceLevel;
  label: string;
  className: string;
}

export function getConfidenceConfig(confidence: number): ConfidenceConfig {
  if (confidence >= 0.8) {
    return { level: 'high', label: 'Ready to review', className: 'confidence-badge--high' };
  }
  if (confidence >= 0.5) {
    return { level: 'medium', label: 'Needs a look', className: 'confidence-badge--medium' };
  }
  return { level: 'low', label: 'Needs your help', className: 'confidence-badge--low' };
}
